import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { TeamAdapter, TeamItemAdapter, TeamItemField } from '@makyoapp/makyo-common'
import { Block16, Edit16 } from '@makyoapp/makyo-icon/icon'
import { FallbackAvatar, Loading, MoreDropdown, ShapeVariant, Table, Td, Th, Tr, TrNotFound } from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { useDeleteOrTransferTeam, useEditTeam } from '@/resolvers/org/teams/api'
import { useCacheContext } from '@/context/CacheContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import { useDateFmt } from '@/hooks/useDateFmt'
import { PROJECT_ROUTE, TEAM_ITEM_FIELDS } from '@/shared/constants'
import { DeleteTeamModal } from '@/components/DeleteTeamModal'
import { StackedAppTd, StackedAvatarTd } from '@/components/TdItem'

const BasicInfoEditModal = lazyModal(() => import('@/components/BasicInfoEditModal'))

/**
 * render the table head
 */
function TeamTableHead() {
  const { t } = useTranslation()
  return (
    <thead>
      <Tr>
        {TEAM_ITEM_FIELDS.map((key) => {
          return (
            <th
              key={key}
              style={{
                width: '23%',
              }}
            >
              <strong className='font-semibold text-light'>{t(key)}</strong>
            </th>
          )
        })}
        <Th className='act'></Th>
      </Tr>
    </thead>
  )
}

/**
 * render the table cell item
 */
function TeamItem(props: { field: TeamItemField; team: TeamItemAdapter }) {
  const { field, team } = props
  const dateFmtDefault = useDateFmt()

  const { imageCache } = useCacheContext()
  const cacheTimestamp = imageCache.values[`team-${team.id}`]
  const withCache = !!cacheTimestamp

  switch (field) {
    case 'created':
      return <Td className='date'>{dateFmtDefault(new Date(team.created))}</Td>
    case 'name':
      return (
        <Td className='name'>
          <Link to={`./${team.id}`} className='flex-c whitespace-nowrap link'>
            <FallbackAvatar
              text={team.name}
              shape={ShapeVariant.Rounded}
              src={withCache ? `${team.avatar}?v=${cacheTimestamp}` : team.avatar}
              alt={team.name}
            />
            <span className='font-medium'>{team.name}</span>
          </Link>
        </Td>
      )
    case 'members':
      return (
        <StackedAvatarTd
          items={team.members
            .filter((m) => m.joined !== undefined)
            .map((member) => ({
              ...member,
              avatar:
                member.avatar && !member.avatar.startsWith('blob:')
                  ? `${member.avatar}?t=${Date.now()}`
                  : member.avatar,
            }))}
        />
      )
    case 'apps':
      return (
        <StackedAppTd
          items={team.apps}
          renderLink={(node, item) => {
            return <Link to={`${PROJECT_ROUTE}/${item.id}`}>{node}</Link>
          }}
        />
      )
    default:
      return <Td className={field}>{team[field]?.toString()}</Td>
  }
}

/**
 * render team table
 */
function TeamTable({
  isLoading,
  setIsLoading,
  onAfterUpdate,
  teams,
}: {
  isLoading: boolean
  setIsLoading: (next: boolean) => void
  onAfterUpdate: () => void
  teams: TeamItemAdapter[]
}) {
  const { t } = useTranslation()
  const { currentOrg } = useCurrentOrgContext()
  const { orgsPerms } = useOrgsContext()
  const [deleteTeam, setDeleteTeam] = useState<TeamItemAdapter | undefined>(undefined)
  const [editTeam, setEditTeam] = useState<TeamItemAdapter | undefined>(undefined)

  const [deleteLoading, setDeleteLoading] = useState(false)

  // Function to handle team update
  const handleEditSubmit = useEditTeam({
    editTeam,
    onBeforeUpdate: useCallback(() => {
      setEditTeam(undefined)
      setIsLoading(true)
    }, [setIsLoading]),
    onAfterUpdate: useCallback(async () => {
      await onAfterUpdate?.()
      setIsLoading(false)
    }, [onAfterUpdate, setIsLoading]),
  })

  // Custom hooks to handle delete or transfer team
  const handleDeleteOrTransferTeam = useDeleteOrTransferTeam()

  // Function to handle delete team
  const handleDeleteTeam = (
    deleteTarget: TeamItemAdapter,
    transferTarget?: TeamAdapter | undefined,
    done?: (() => void) | undefined
  ) => {
    handleDeleteOrTransferTeam(deleteTarget, transferTarget, {
      onBeforeUpdate: () => setDeleteLoading(true),
      onAfterUpdate: async () => {
        done?.()
        await onAfterUpdate?.()
        setDeleteLoading(false)
      },
      transferredNotice: t('Apps transferred!'),
      deletedNotice: t('Team removed!'),
    })
  }

  return (
    <>
      <Table className='border bdr'>
        <TeamTableHead />
        <tbody>
          {isLoading ? (
            <Tr>
              <Td colSpan={TEAM_ITEM_FIELDS.length + 1}>
                <Loading fullHeight={false} />
              </Td>
            </Tr>
          ) : teams.length < 1 ? (
            <TrNotFound colSpan={TEAM_ITEM_FIELDS.length + 1} />
          ) : (
            teams.map((row, rowIdx) => {
              const currentPerms = orgsPerms[row.org.id]
              const canEditTeam = currentPerms?.[OrgPermission.TeamsEditTeamInfo]

              const canRemoveTeam = !row.isDefault && currentPerms?.[OrgPermission.TeamsDeleteTeams] === true

              const hideMenu = !(canRemoveTeam || canEditTeam)

              return (
                <Tr key={`tbl-row-${rowIdx}`}>
                  {TEAM_ITEM_FIELDS.map((k) => (
                    <TeamItem key={`${k}-${rowIdx}`} field={k} team={row} />
                  ))}
                  <Td className='act' align='right'>
                    {!hideMenu && (
                      <MoreDropdown id='team-more' placement='bottom-end' menuClassName='text-m-m' portalMenu={true}>
                        {canEditTeam && (
                          <MoreDropdown.MenuItem
                            id={`${row.id}-edit-act`}
                            className='team-edit-act'
                            onClick={() => setEditTeam(row)}
                          >
                            <Edit16 />
                            <span>{t('Edit')}</span>
                          </MoreDropdown.MenuItem>
                        )}
                        {canRemoveTeam && (
                          <MoreDropdown.MenuItem
                            id={`${row.id}-delete-act`}
                            className='team-delete-act red'
                            onClick={() => setDeleteTeam(row)}
                          >
                            <Block16 />
                            <span>{t('Remove')}</span>
                          </MoreDropdown.MenuItem>
                        )}
                      </MoreDropdown>
                    )}
                  </Td>
                </Tr>
              )
            })
          )}
        </tbody>
      </Table>
      {deleteTeam && currentOrg && (
        <DeleteTeamModal
          id='delete-team-modal'
          setDeleteTeam={setDeleteTeam}
          deleteTeam={deleteTeam}
          onSubmit={handleDeleteTeam}
          isLoading={deleteLoading}
        />
      )}
      {editTeam && (
        <BasicInfoEditModal
          show={true}
          onHide={() => setEditTeam(undefined)}
          name={editTeam.name}
          avatar={editTeam.avatar}
          title={t('Edit Team')}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  )
}

export default TeamTable
